import { useEffect, useState } from "react";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { cn } from "@/lib/utils";

type HermesThinkingIndicatorProps = {
  className?: string;
  /** 真实工具/流水线状态；有则展示并隐藏轮换短句。 */
  detail?: string | null;
};

/** 流式等待时轮换的短句（小白可读，轻松不油腻）。 */
export const HERMES_THINKING_PHRASES = [
  "想一下…",
  "整理中…",
  "马上好…",
  "翻翻看…",
  "理一理…",
  "快好了…",
  "再想想…",
] as const;

/** 文案轮换间隔（毫秒）。 */
export const HERMES_THINKING_ROTATE_MS = 2500;

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  return reduced;
}

/** 按固定间隔轮换短句；卸载时清 timer。 */
function useRotatingThinkingPhrase(): string {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % HERMES_THINKING_PHRASES.length);
    }, HERMES_THINKING_ROTATE_MS);
    return () => window.clearInterval(id);
  }, []);

  return HERMES_THINKING_PHRASES[index] ?? HERMES_THINKING_PHRASES[0];
}

/** 遗留英文工具名泄漏（状态条不得回显 function.name）。 */
const TOOL_NAME_LEAK =
  /\b(?:materials_|kb_|brand_site_|journey_|webfetch|websearch|fetch_url|skills_list|skill_view|hermes_)/;

/** 丢掉僵硬默认句与工具名泄漏，只保留有意义的动作文案。 */
export function softenHermesStreamDetail(status: string | null | undefined): string | null {
  if (!status?.trim()) return null;
  const trimmed = status.trim();
  if (trimmed === "Hermes 正在回复…" || trimmed === "Hermes 正在回复") return null;
  if (trimmed === "正在处理…" || trimmed === "正在处理") return null;
  if (trimmed.startsWith("正在思考")) return null;
  if (TOOL_NAME_LEAK.test(trimmed)) return null;
  return trimmed;
}

/**
 * 流式等待指示：三点脉动 + 轮换短句；有真实工具 status 时只显示该文案。
 */
export function HermesThinkingIndicator({
  className,
  detail = null,
}: HermesThinkingIndicatorProps) {
  const reducedMotion = usePrefersReducedMotion();
  const softDetail = softenHermesStreamDetail(detail);
  const phrase = useRotatingThinkingPhrase();
  // 有明确动作文案时隐藏「整理中…」类轮询句，避免叠两行状态。
  const showRotatingPhrase = !softDetail;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={cn(
        "flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-muted-foreground",
        className,
      )}
    >
      {/* 主文案稳定，轮换句仅视觉展示，避免读屏反复打断 */}
      <span className="sr-only">
        {softDetail ? `Hermes 处理中：${softDetail}` : "Hermes 处理中"}
      </span>
      <span
        aria-hidden="true"
        className={cn(
          "hermes-thinking-dots inline-flex shrink-0 items-center gap-[3px]",
          reducedMotion && "hermes-thinking-dots--static",
        )}
      >
        <span className="hermes-thinking-dot" />
        <span className="hermes-thinking-dot" />
        <span className="hermes-thinking-dot" />
      </span>
      {showRotatingPhrase ? (
        reducedMotion ? (
          <span
            aria-hidden="true"
            className="text-[13px] leading-none tracking-wide text-muted-foreground opacity-80"
          >
            {phrase}
          </span>
        ) : (
          <span aria-hidden="true" className="inline-flex">
            <Shimmer
              as="span"
              className="text-[13px] leading-none tracking-wide"
              duration={1.6}
              spread={2.2}
            >
              {phrase}
            </Shimmer>
          </span>
        )
      ) : null}
      {softDetail ? (
        <span
          aria-hidden="true"
          className="text-[13px] leading-none tracking-wide text-muted-foreground/90"
        >
          {softDetail}
        </span>
      ) : null}
    </div>
  );
}
